import React, { useState, useEffect, Suspense } from 'react';
import { Outlet, NavLink, useLocation, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, ShoppingCart, BookOpen, Package, User, X, Settings, LogOut,
  Headphones, Megaphone, History, IndianRupee,
  ChevronRight, Calendar, Store, ShieldOff, MoreHorizontal
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { useConfirm } from '../../components/ConfirmModal';
import AppFooter from '../../components/AppFooter';
import AnnouncementSlider from '../AnnouncementSlider';
import { PageSkeletonFallback } from '../LoadingSkeleton';
import { db, doc, cachedGetDoc } from '../../services/firebase';
import { useFlags } from '../../context/FeatureFlags';
import { APP_CONFIG } from '../../utils/config';
import logo from '../../assets/logo.png';
import { getRetailerAvatar } from '../../utils/avatar';

const navItems = [
  { to: '/portal',    label: 'Home',         Icon: Home,        end: true },
  { to: '/order',     label: 'Place Order',  Icon: ShoppingCart },
  { to: '/track',     label: 'My Orders',    Icon: Package },
  { to: '/history',   label: 'Order History',Icon: History },
  { to: '/prices',    label: 'Price List',   Icon: IndianRupee },
  { to: '/my-ledger', label: 'My Ledger',    Icon: BookOpen,    flag: 'ledgerView' },
  { to: '/profile',   label: 'My Account',   Icon: User },
  { to: '/support',   label: 'Support',      Icon: Headphones,  flag: 'supportTickets' },
];

const blockedNavItems = [
  { to: '/portal',  label: 'Account Status', Icon: ShieldOff,   end: true },
  { to: '/support',  label: 'Support & Help', Icon: Headphones },
];

const blockedBottomNav = [
  { to: '/portal',  label: 'Status',  Icon: ShieldOff,   end: true },
  { to: '/support', label: 'Support', Icon: Headphones },
];

const moreCategories = [
  {
    category: 'Orders & Trading',
    links: [
      { to: '/portal',  label: 'Home',          desc: 'Dashboard & live dispatch status', Icon: Home, end: true },
      { to: '/order',   label: 'Place Order',   desc: 'Daily milk & dairy order',      Icon: ShoppingCart },
      { to: '/track',   label: 'Live Orders',   desc: "Track today's live delivery",   Icon: Package },
      { to: '/history', label: 'Order History', desc: 'Dispatched crates & invoices',  Icon: History },
      { to: '/prices',  label: 'Price List',    desc: 'Amrit Dairy wholesale rates',   Icon: IndianRupee },
    ],
  },
  {
    category: 'Accounts & Finance',
    links: [
      { to: '/my-ledger', label: 'My Ledger',  desc: 'Credit ledger & balance sheet', Icon: BookOpen, flag: 'ledgerView' },
      { to: '/profile',   label: 'My Account', desc: 'Store profile & settings',      Icon: User },
    ],
  },
  {
    category: 'Assistance & Network',
    links: [
      { to: '/support', label: 'Help & Support',    desc: 'Dispute tickets & assistance', Icon: Headphones, flag: 'supportTickets' },
      { to: '/about',   label: 'About Lucy Garden', desc: 'Supply network & contacts',   Icon: Store },
    ],
  },
];

const PAGE_TITLES = {
  '/portal': 'Home', '/': 'Home', '/order': 'Place Order', '/checkout': 'Checkout',
  '/track': 'My Orders', '/history': 'Order History', '/my-ledger': 'My Ledger',
  '/prices': 'Price List', '/profile': 'My Account', '/settings': 'My Account',
  '/support': 'Support', '/about': 'About',
  '/privacy': 'Privacy Policy', '/terms': 'Terms',
};

export default function RetailerLayout() {
  const [navHidden, setNavHidden] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const closeDrawer = () => setMoreOpen(false);

  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('lg_user') || '{}');
  const confirm = useConfirm();
  const flags = useFlags();

  const isBlocked = !!user.blocked;
  const activeNavItems = isBlocked
    ? blockedNavItems
    : navItems.filter(item => !item.flag || flags[item.flag] !== false);

  const activeBottomNav = isBlocked
    ? blockedBottomNav
    : [
        { to: '/portal',    label: 'Home',   Icon: Home,        end: true },
        { to: '/order',     label: 'Order',  Icon: ShoppingCart },
        { to: '/track',     label: 'Track',  Icon: Package },
        flags.ledgerView !== false
          ? { to: '/my-ledger', label: 'Ledger', Icon: BookOpen }
          : { to: '/history',   label: 'History', Icon: History },
      ];

  const pageTitle = isBlocked && location.pathname === '/'
    ? 'Account Suspended'
    : PAGE_TITLES[location.pathname] || navItems.find(n => n.to === location.pathname)?.label || 'Lucy Garden';

  const currentItem = activeNavItems.find(n => n.to === location.pathname);
  const PageIcon = currentItem?.Icon || (isBlocked ? ShieldOff : Store);

  const todayFormatted = new Date().toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  useEffect(() => {
    import('../TopProgressBar').then(m => { m.triggerProgress(); setTimeout(m.stopProgress, 300); });
  }, [location.pathname]);

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (moreOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [moreOpen]);

  useEffect(() => {
    let lastY = 0;
    const h = () => { const y = window.scrollY; setNavHidden(y > lastY && y > 60); lastY = y; };
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const handleLogout = async () => {
    const ok = await confirm({ title: 'Logout', message: 'Are you sure you want to logout?', confirmText: 'Logout', type: 'logout' });
    if (ok) {
      const phone = sessionStorage.getItem('lg_active_phone');
      if (phone) { localStorage.removeItem(`lg_user_${phone}`); localStorage.removeItem(`lg_last_verify_${phone}`); }
      localStorage.removeItem('lg_user'); sessionStorage.removeItem('lg_active_phone'); window.location.reload();
    }
  };

  return (
    <div className="min-h-screen flex bg-[#faf9f6] dark:bg-[#11100f] text-[#1a1917] dark:text-[#f5f4f0]">

      {/* ── Desktop Sidebar ── */}
      <aside
        onMouseEnter={() => setSidebarExpanded(true)}
        onMouseLeave={() => setSidebarExpanded(false)}
        className={`hidden lg:flex fixed top-0 left-0 h-full z-50 flex-col bg-white dark:bg-[#161514] border-r border-[#e8e6e1] dark:border-[#2a2826] transition-all duration-200 select-none ${
          sidebarExpanded ? 'w-[216px]' : 'w-[60px]'
        }`}>

        {/* Logo & Partner Badge */}
        <div className="h-[56px] flex items-center px-3.5 border-b border-[#e8e6e1] dark:border-[#2a2826] shrink-0 bg-white/50 dark:bg-[#161514]/50 backdrop-blur-xs">
          <div className="w-8 h-8 rounded-xl overflow-hidden border border-[#e2e0db] dark:border-[#2a2826] bg-white shrink-0 p-0.5 shadow-2xs">
            <img src={logo} alt="Lucy Garden" className="w-full h-full object-cover rounded-lg" />
          </div>
          {sidebarExpanded && (
            <div className="ml-2.5 overflow-hidden">
              <div className="flex items-center gap-1.5">
                <p className="font-bold text-[#1a1917] dark:text-[#f5f4f0] text-sm whitespace-nowrap leading-tight tracking-tight">Lucy Garden</p>
                <span className="text-[9px] font-extrabold uppercase tracking-wider bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.2 rounded border border-emerald-500/25 shrink-0">
                  Retail
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isBlocked ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'}`} />
                <p className={`text-[10px] whitespace-nowrap font-medium leading-none ${isBlocked ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-[#78716c] dark:text-[#a8a49c]'}`}>
                  {isBlocked ? 'Access Restricted' : 'Partner Portal'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
          {activeNavItems.map((item, idx) => (
            <React.Fragment key={item.to}>
              {sidebarExpanded && idx === 0 && (
                <div className="px-2.5 pt-1 pb-1 text-[9px] font-bold tracking-wider text-[#a8a29e] dark:text-[#78716c] uppercase select-none">
                  Quick Access
                </div>
              )}
              {sidebarExpanded && idx === 4 && (
                <div className="px-2.5 pt-2.5 pb-1 text-[9px] font-bold tracking-wider text-[#a8a29e] dark:text-[#78716c] uppercase select-none border-t border-[#e8e6e1]/80 dark:border-[#2a2826]/80 mt-2">
                  Pricing & Ledger
                </div>
              )}
              {sidebarExpanded && idx === 6 && (
                <div className="px-2.5 pt-2.5 pb-1 text-[9px] font-bold tracking-wider text-[#a8a29e] dark:text-[#78716c] uppercase select-none border-t border-[#e8e6e1]/80 dark:border-[#2a2826]/80 mt-2">
                  Account & Help
                </div>
              )}
              {!sidebarExpanded && (idx === 4 || idx === 6) && (
                <div className="my-2 border-t border-[#e8e6e1] dark:border-[#2a2826] mx-1" />
              )}
              <NavLink to={item.to} end={item.end} title={item.label}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-2.5 py-2.5 rounded-xl text-xs font-semibold transition-all duration-150 ${
                    isActive
                      ? 'bg-emerald-500/12 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 font-bold border border-emerald-500/30 shadow-2xs'
                      : 'text-[#59554e] dark:text-[#a8a49c] hover:bg-[#f4f2ed] dark:hover:bg-[#201f1d] hover:text-[#1a1917] dark:hover:text-[#f5f4f0]'
                  } ${!sidebarExpanded ? 'justify-center px-0' : ''}`}>
                {({ isActive }) => (
                  <>
                    {/* Left Active Indicator Accent Bar */}
                    {isActive && (
                      <motion.div
                        layoutId="retailerActivePill"
                        className="absolute left-0 top-2 bottom-2 w-1 bg-emerald-600 dark:bg-emerald-400 rounded-r-full"
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      />
                    )}
                    <item.Icon
                      size={17}
                      strokeWidth={isActive ? 2.3 : 1.8}
                      className={`shrink-0 ${
                        isActive
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-[#78716c] dark:text-[#948f86]'
                      }`}
                    />
                    {sidebarExpanded && (
                      <span className="whitespace-nowrap flex-1 tracking-tight">{item.label}</span>
                    )}
                  </>
                )}
              </NavLink>
            </React.Fragment>
          ))}
        </nav>

        {/* Retailer Profile Card & Clean Logout */}
        <div className="p-2 border-t border-[#f0eee9] dark:border-[#22211f] shrink-0">
          {sidebarExpanded ? (
            <div className="p-2 rounded-xl bg-[#f7f6f3] dark:bg-[#1c1a19] border border-[#e8e6e1] dark:border-[#2a2826] shadow-2xs">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 border border-black/10 dark:border-white/10 flex items-center justify-center shadow-xs">
                  <img src={getRetailerAvatar(user.name || user.phone)} alt="Retailer" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-[#1a1917] dark:text-[#f5f4f0] truncate leading-tight pt-0.5">{user.name || 'Retail Partner'}</p>
                  <p className="text-[10px] text-[#78716c] dark:text-[#a8a49c] truncate leading-normal mt-0.5">{user.area || 'Retailer'}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full mt-2 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-red-500/10 text-red-600 dark:text-red-400 text-[11px] font-bold hover:bg-red-500/20 transition-colors border border-red-500/15 cursor-pointer"
              >
                <LogOut size={13} strokeWidth={2} />
                <span>Logout</span>
              </button>
            </div>
          ) : (
            <div className="px-1.5 py-1">
              <button
                onClick={handleLogout}
                title="Logout"
                className="w-full h-9 rounded-lg flex items-center justify-center text-red-600 dark:text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors border border-red-500/15 cursor-pointer"
              >
                <LogOut size={15} strokeWidth={2} />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ── Main Content ── */}
      <div className={`flex-1 transition-all duration-200 ${sidebarExpanded ? 'lg:ml-[216px]' : 'lg:ml-[60px]'} flex flex-col min-h-screen min-w-0`}>

        {/* Mobile Header: Sleek, lightweight, Midnight Navy */}
        <header className="sticky top-0 z-40 lg:hidden bg-[#152438] text-white border-b border-[#22354e] shadow-xs" id="retailer-mobile-header">
          <div className="flex items-center justify-between px-3.5 h-[50px]">
            <Link to="/portal" className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-lg overflow-hidden border border-white/20 bg-white/10 shrink-0 p-0.5">
                <img src={logo} alt="Lucy Garden" className="w-full h-full object-cover rounded-md" />
              </div>
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="font-bold text-white text-xs tracking-tight truncate">Lucy Garden</span>
                <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded border border-emerald-400/30 shrink-0">
                  Retail
                </span>
              </div>
            </Link>

            <span className="text-[11px] font-mono text-white/60 shrink-0">
              {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
            </span>
          </div>
        </header>

        {/* Desktop Header: Structured Enterprise System Bar */}
        <header className="sticky top-0 z-30 hidden lg:flex items-center justify-between px-6 h-[56px] bg-white dark:bg-[#161514] border-b border-[#dedbd5] dark:border-[#2a2826] shadow-[0_1px_3px_rgba(0,0,0,0.03)]">
          {/* Left: Active Section Icon & Breadcrumb */}
          <div className="flex items-center gap-2.5 min-w-0">
            <PageIcon size={16} className="text-[#1e3a5f] dark:text-[#60a5fa] shrink-0" strokeWidth={2.2} />
            <div className="flex items-center gap-2 text-xs">
              <Link to="/portal" className="text-[#78716c] hover:text-[#1a1917] dark:text-[#8c887e] dark:hover:text-[#f5f4f0] transition-colors font-medium">
                Lucy Garden
              </Link>
              <ChevronRight size={13} className="text-[#a8a49c] dark:text-[#555]" />
              <span className="font-bold text-[#1a1917] dark:text-[#f5f4f0] text-sm tracking-tight">
                {pageTitle}
              </span>
            </div>
          </div>

          {/* Right: Date & User Identity */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-1.5 text-xs text-[#78716c] dark:text-[#a8a49c] font-medium font-mono px-2">
              <Calendar size={13} className="text-[#8c867e]" />
              <span>{todayFormatted}</span>
            </div>

            <Link
              to="/profile"
              className="flex items-center gap-2.5 pl-1.5 pr-3.5 py-1 rounded-full bg-[#f7f6f3] dark:bg-[#1f1e1c] border border-[#e4e2dc] dark:border-[#2c2a27] hover:border-[#1e3a5f]/30 transition-colors">
              <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 border border-black/10 dark:border-white/10 flex items-center justify-center shadow-xs">
                <img src={getRetailerAvatar(user.name || user.phone)} alt="Retailer" className="w-full h-full object-cover" />
              </div>
              <div className="flex flex-col text-left justify-center py-0.5 min-w-0">
                <span className="text-xs font-semibold text-[#1a1917] dark:text-[#f5f4f0] leading-normal pt-0.5 truncate max-w-[140px] block">
                  {user.name || 'Retail Partner'}
                </span>
                <span className="text-[10px] text-[#8c827a] font-medium leading-normal block -mt-0.5">
                  {user.area || 'Retailer'}
                </span>
              </div>
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-3.5 sm:p-4 md:p-5 pb-4 lg:pb-6 w-full">

          <div className="animate-fade-in">
            {/* Multi-Banner Offer/Notice Carousel Slider */}
            <AnnouncementSlider />

            <Suspense fallback={<PageSkeletonFallback type="retailer" />}>
              <motion.div key={location.pathname} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.1 }}>
                <Outlet />
              </motion.div>
            </Suspense>
          </div>
        </main>

        <AppFooter type="retailer" />
      </div>

      {/* ── Enterprise Mobile Side Navigation Drawer (Off-Canvas Left Drawer) ── */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {moreOpen && (
            <motion.div
              key="retailer-drawer-overlay-wrapper"
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[99999] lg:hidden overflow-hidden"
            >
              {/* Backdrop */}
              <motion.div
                key="retailer-drawer-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={closeDrawer}
                className="absolute inset-0 bg-black/60 backdrop-blur-xs"
              />

              {/* Slide Drawer Panel (Anchored to LEFT Edge) */}
              <motion.aside
                key="retailer-drawer-panel"
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 320 }}
                style={{ left: 0, right: 'auto' }}
                className="absolute top-0 bottom-0 left-0 right-auto z-10 w-[285px] max-w-[82vw] h-full bg-white dark:bg-[#121110] border-r border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col justify-between overflow-hidden"
              >
                {/* Top Header */}
                <div className="px-4 py-4 border-b border-gray-100 dark:border-gray-800/80 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-3 min-w-0 flex-1 mr-2">
                    <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 border-2 border-black/10 dark:border-white/10 flex items-center justify-center shadow-sm">
                      <img src={getRetailerAvatar(user.name || user.phone)} alt="Retailer" className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[14px] font-bold text-gray-900 dark:text-gray-100 truncate leading-normal pt-1 pb-0.5">
                        {user.shopName || user.name || 'Retail Partner'}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isBlocked ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'}`} />
                        <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 truncate">
                          {isBlocked ? 'Access Restricted' : (user.area ? `${user.area} · Retail Partner` : 'Authorized Retailer')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={closeDrawer}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-800 transition-colors shrink-0"
                    aria-label="Close menu"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Seamless Flat Navigation List */}
                <div className="flex-1 overflow-y-auto px-2.5 py-2 space-y-3">
                  {moreCategories.map((group, idx) => {
                    const filteredLinks = group.links.filter(l => !l.flag || flags[l.flag] !== false);
                    if (filteredLinks.length === 0) return null;
                    return (
                      <div key={group.category}>
                        {idx > 0 && <div className="h-px bg-gray-100 dark:bg-gray-800/60 my-2 mx-1" />}
                        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 px-3 py-1">
                          {group.category}
                        </p>
                        <div className="space-y-0.5">
                          {filteredLinks.map((link) => {
                            const isActive = location.pathname === link.to;
                            return (
                              <button
                                key={link.to}
                                onClick={() => { closeDrawer(); navigate(link.to); }}
                                className={`w-full group flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-colors touch-manipulation ${
                                  isActive
                                    ? 'bg-[#1e3a5f] text-white font-semibold shadow-xs'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-white/5 active:bg-gray-100'
                                }`}
                              >
                                <div className="flex items-center gap-3 min-w-0">
                                  <link.Icon
                                    size={18}
                                    strokeWidth={isActive ? 2.2 : 1.8}
                                    className={isActive ? 'text-white' : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200'}
                                  />
                                  <div className="text-left min-w-0">
                                    <p className="text-[13px] font-medium tracking-tight truncate leading-tight">
                                      {link.label}
                                    </p>
                                    {link.desc && (
                                      <p className={`text-[10px] truncate leading-tight mt-0.5 ${isActive ? 'text-blue-100/80' : 'text-gray-400 dark:text-gray-500'}`}>
                                        {link.desc}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <ChevronRight
                                  size={14}
                                  className={`transition-transform shrink-0 ${
                                    isActive ? 'text-white/80' : 'text-gray-400 dark:text-gray-500 group-hover:translate-x-0.5'
                                  }`}
                                />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Bottom Clean Footer */}
                <div className="p-3 border-t border-gray-100 dark:border-gray-800/80 shrink-0">
                  <button
                    onClick={() => { closeDrawer(); handleLogout(); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 text-sm font-semibold transition-colors"
                  >
                    <LogOut size={17} strokeWidth={1.8} />
                    <span>Logout Session</span>
                  </button>
                  <p className="text-center text-[10px] text-gray-400 dark:text-gray-500 pt-2 font-medium">
                    Lucy Garden Retail · v{APP_CONFIG.version}
                  </p>
                </div>
              </motion.aside>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* ── Mobile Bottom Navigation (Grounded Enterprise Toolbar) ── */}
      <nav className={`fixed bottom-0 left-0 right-0 w-full z-40 lg:hidden transition-transform duration-300 ${navHidden ? 'translate-y-full' : 'translate-y-0'} bg-white dark:bg-[#121110] border-t border-[#e2e0db] dark:border-[#262524] shadow-[0_-2px_10px_rgba(0,0,0,0.03)] pb-[env(safe-area-inset-bottom)]`}>
        <div className="flex items-center justify-around h-[54px] px-1">
          {activeBottomNav.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end} className="relative flex-1 flex justify-center py-1">
              {({ isActive }) => (
                <div className={`flex flex-col items-center justify-center gap-0.5 relative transition-colors ${isActive ? 'text-[#1e3a5f] dark:text-[#60a5fa]' : 'text-[#64748b] dark:text-[#94a3b8] hover:text-[#1a1917]'}`}>
                  {/* Subtle top indicator line on active tab */}
                  {isActive && (
                    <motion.div
                      layoutId="retailerBottomActiveLine"
                      className="absolute -top-[5px] w-6 h-[2.5px] bg-[#1e3a5f] dark:bg-[#60a5fa] rounded-full"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <div className="relative">
                    <item.Icon size={20} strokeWidth={isActive ? 2.2 : 1.7} />
                  </div>
                  <span className={`text-[10px] tracking-tight ${isActive ? 'font-bold' : 'font-medium'}`}>
                    {item.label}
                  </span>
                </div>
              )}
            </NavLink>
          ))}
          <button
            onClick={() => setMoreOpen(prev => !prev)}
            className="relative flex-1 flex justify-center py-1"
          >
            <div className={`flex flex-col items-center justify-center gap-0.5 relative transition-colors ${moreOpen ? 'text-[#1e3a5f] dark:text-[#60a5fa]' : 'text-[#64748b] dark:text-[#94a3b8] hover:text-[#1a1917]'}`}>
              {moreOpen && (
                <motion.div
                  layoutId="retailerBottomActiveLine"
                  className="absolute -top-[5px] w-6 h-[2.5px] bg-[#1e3a5f] dark:bg-[#60a5fa] rounded-full"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <div className="relative flex items-center justify-center">
                <MoreHorizontal size={20} strokeWidth={moreOpen ? 2.2 : 1.7} />
              </div>
              <span className={`text-[10px] tracking-tight ${moreOpen ? 'font-bold' : 'font-medium'}`}>
                More
              </span>
            </div>
          </button>
        </div>
      </nav>

    </div>
  );
}
